/**
 * useStudentActivityView — l'écran d'une activité, prêt à poser : le moteur de l'exécution
 * (`useActivityRun`), la figure de l'énoncé, l'appareil photo (`useCopyScanner` : au téléphone le
 * scanner GeniusScan, dans un navigateur la caméra du design system), l'état de l'écran, et ce qu'ils
 * donnent en props de `<StudentActivityPage>`, de la correction et des félicitations.
 *
 * C'est la pièce que l'écran du live compose : il passe l'activité poussée, ses entrées
 * (`ActivityLiveInputs`) et `heading="inline"` sans `onBack`, puis pose `<StudentActivityBody>`. Hors
 * live, l'écran `activity` (et `sam-session`, Sam en plus) fait de même avec la route.
 *
 * La porte du quiz de prérequis se pose ici (`usePrerequisiteGate`), pour valoir partout où
 * l'activité se compose, en live comme hors live : tant que le quiz n'est pas passé, le chrono est
 * arrêté (comme l'ancien `hasQuizOverlay`) et `<StudentActivityBody>` pose la feuille du quiz. La
 * tentative faite, la même feuille montre la correction (le chrono attend encore qu'elle se ferme),
 * puis « Revoir le quiz » la rouvre (`page.onReviewQuiz`).
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Platform } from "react-native"
import { useSelector } from "react-redux"

import { type StartCountdownKind } from "../../../../../../design-system/student"
import { type StudentActivityPageProps } from "../../../../../../design-system/pages/StudentActivityPage"
import { getMostRecentActivity, getTodaysFinishedActivities } from "../../../../../../data/utility/dataUtils"
import { type CopyFile } from "../../../../../../engine/activity/copy/copyOutbox"
import { usePrerequisiteGate } from "../../../../../../engine/activity/prerequisite/usePrerequisiteGate"
import { type ApiCheckpointConversation } from "../../../../../../engine/precall/checkpoint/checkpointConversationMachine"
import { useActivityRun, type ActivityLiveInputs } from "../../../../../../engine/activity/useActivityRun"
import { useStatementFigure } from "../../../../../../engine/activity/useStatementFigure"
import { createTelemetry } from "../../../../../../engine/shared/telemetry"
import {
    activityTemplateSteps,
    addMaterialLabel,
    remainingTodo,
    toStudentActivityHeading,
    toStudentActivityStatement,
    type ApiActivityTemplate,
    type ApiRunActivity,
} from "../../../../adapters/student/Activity/studentActivityAdapter"
import {
    isFinishedActivity,
    type ApiFinishedActivity,
} from "../../../../adapters/student/Activity/finishedActivityAdapter"
import { type ApiSchoolSubject } from "../../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    DELETE_FAILED_DIALOG,
    DELETED_DIALOG,
    exitDialog,
    finishFailureDialog,
    studentActivityCopy,
    type StudentActivityDialog,
} from "../../../../view-models/student/Activity/studentActivityViewModel"
import {
    studentActivityCelebration,
    studentActivityCongratsModel,
    type StudentActivityCongratsModel,
} from "../../../../view-models/student/Activity/studentActivityCongratsViewModel"
import { DIALOG_CLOSE, trackDialogButton } from "../../../hooks/trackDialogButton"
import { ACTIVITY_BUTTONS, trackButton, trackCopyCapture } from "./activityAnalytics"
import { type UIConversationMessage } from "../../../types/studentConversation"
import { useConversationVoicePlayer } from "../Messaging/useConversationVoicePlayer"
import { useCopyScanner } from "./useCopyScanner"
import { useStudentActivityState } from "./useStudentActivityState"
import { selectDisplayedStudent } from "../../../../view-models/shared/displayedStudent"

type ActivityRoot = {
    data?: {
        staticData?: { activity_templates?: ApiActivityTemplate[]; school_subjects?: ApiSchoolSubject[] }
        currentUser?: {
            object?: {
                id?: number
                xp?: number
                display_name?: string
                todo_activities?: ApiRunActivity[]
                activities?: ApiRunActivity[]
            } | null
        }
    }
}

/** Les entrées du live, et `activity_id` : l'attribut de l'activité OUVERTE, relue comprise, que l'exécution ne voit pas. */
export type StudentActivityLiveInputs = ActivityLiveInputs & {
    setActivityAttribute?: (activityId: number | null) => void
}

export type StudentActivityViewOptions = {
    activity: ApiRunActivity | null
    live?: StudentActivityLiveInputs
    /** `inline` dans le live (le titre et le chrono défilent avec l'énoncé). */
    heading?: "bar" | "inline"
    /** Absent dans le live : pas de retour. */
    canGoBack?: boolean
    /**
     * « Ajouter un énoncé » (sans support). Absent : en live, la route `new-material` par-dessus la
     * séance, comme l'ancien `TimerActivityStep` ; hors live, pas de lien.
     */
    onAddMaterial?: (activityId: number) => void
    /** L'activité était menée avec Sam (le champion qui célèbre, « … avec Sam »). */
    withSam?: boolean
    /** Une activité suivante est prévue par l'appelant (la séquence de Sam). */
    nextActivityId?: number | null
    /** Le quiz de prérequis est passé (en live : prévenir le tuteur, `agentConversationCompleted`). */
    onPrerequisiteQuizCompleted?: (result: ApiCheckpointConversation) => void
    /** Pas d'entrée (l'annonce et le décompte) : l'ancien `skipInitAnimation`. */
    skipIntro?: boolean
}

// Un énoncé PDF qui ne se charge pas, signalé comme l'ancien `PdfViewer` (`notifyBugsnag`, avec
// l'énoncé en contexte).
const statementTelemetry = createTelemetry("activity.statement", { role: "student" })

export type StudentActivityViewDialog = StudentActivityDialog & {
    onConfirm: () => void
    onCancel: () => void
    /** Le voile ou le retour d'Android : ferment comme l'annulation, sans mesure. */
    onDismiss: () => void
}

/**
 * Les gestes d'une boîte, mesurés comme l'ancienne `DialogM` : ses boutons seulement (le voile et le
 * retour ne la fermaient pas), et rien pour un bouton qu'elle n'avait pas.
 */
function trackedDialog(
    dialog: StudentActivityDialog,
    onConfirm: () => void,
    onCancel: () => void
): StudentActivityViewDialog {
    return {
        ...dialog,
        onConfirm: () => {
            trackDialogButton(dialog.title, dialog.confirmLabel)
            onConfirm()
        },
        onCancel: () => {
            if (dialog.cancelLabel != null) {
                trackDialogButton(dialog.title, dialog.cancelIsClose ? DIALOG_CLOSE : dialog.cancelLabel)
            }
            onCancel()
        },
        onDismiss: onCancel,
    }
}

export function useStudentActivityView(options: StudentActivityViewOptions) {
    const { activity: opened, live, heading = "bar", canGoBack = true, onAddMaterial, withSam = false } = options

    const navigation = useNavigation() as unknown as { push: (route: string, params?: object) => void }
    const addMaterial =
        onAddMaterial ??
        (live != null ? (activityId: number) => navigation.push("new-material", { activityId }) : undefined)

    const templates = useSelector((state: ActivityRoot) => state.data?.staticData?.activity_templates)
    const subjects = useSelector((state: ActivityRoot) => state.data?.staticData?.school_subjects)
    const user = useSelector((state: ActivityRoot) => selectDisplayedStudent(state))

    // En live, une activité déjà rendue que le tuteur affiche (hors de la liste à faire, statut autre
    // que « à faire » : la règle d'`ActivityWrapperScreen`) se relit, elle ne se relance pas : rien de
    // l'exécution ne tourne pour elle (ni chrono, ni `update-chrono`, ni « J'ai fini »). Hors live,
    // l'écran de la route choisit déjà, par l'état posé avec l'activité. Lu à son ouverture : celle
    // qu'on vient de rendre change de statut pendant sa correction et ses félicitations, et ne bascule
    // pas pour autant.
    const finishedAtOpen = useMemo(
        () =>
            live != null &&
            isFinishedActivity(opened) &&
            !(user?.todo_activities ?? []).some((todo) => todo?.id === opened?.id),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [opened?.id, live != null]
    )
    const activity = finishedAtOpen ? null : opened

    const steps = useMemo(() => activityTemplateSteps(activity, templates), [activity, templates])
    const quiz = usePrerequisiteGate({
        activity,
        liveSessionId: live?.liveSessionId ?? null,
        onQuizCompleted: options.onPrerequisiteQuizCompleted,
    })
    /* ── l'entrée : l'annonce, et le décompte d'une activité neuve, avant que le chrono parte ── */
    // Comme l'ancien `TimerActivityStep`, à chaque activité ouverte pour être faite : « Ton activité
    // va commencer... » puis 3, 2, 1 ; « Ton activité va reprendre... » quand le serveur a déjà un
    // chrono pour elle. Pas d'entrée après le quiz de prérequis (l'ancien la passait quand le quiz
    // s'était posé), ni pour une activité qu'on relit.
    const introActivityId = typeof activity?.id === "number" ? activity.id : null
    const [introDoneFor, setIntroDoneFor] = useState<number | null>(null)
    const quizShownRef = useRef<number | null>(null)
    if (quiz.state.status === "quiz" && introActivityId != null) quizShownRef.current = introActivityId
    const introKind: StartCountdownKind | null =
        introActivityId != null &&
        !options.skipIntro &&
        introDoneFor !== introActivityId &&
        quizShownRef.current !== introActivityId &&
        quiz.state.status === "passed"
            ? (activity?.duration ?? 0) > 0
                ? "resume"
                : "start"
            : null
    const introOpen = introKind != null && introActivityId != null

    // Tant que la porte tient, ou que l'entrée se joue, le chrono est arrêté, comme la pause du tuteur.
    const holdChrono = quiz.blocking || introOpen
    const runLive = useMemo(() => (holdChrono ? { ...live, paused: true } : live), [holdChrono, live])
    const run = useActivityRun({ activity, templateSteps: steps, live: runLive })
    // Une écriture par activité ouverte, ici et pas dans l'exécution : une activité relue ne la fait pas tourner, et
    // le passage étranglé des attributs part en tête. Un vide écrit avant l'id se lirait 2 s chez le masteur (son
    // bouton, sa vue de l'élève épinglé). Le setter change avec le mode : seule l'activité réveille cet effet.
    const openedId = typeof opened?.id === "number" ? opened.id : null
    const setActivityAttributeRef = useRef(live?.setActivityAttribute)
    setActivityAttributeRef.current = live?.setActivityAttribute
    useEffect(() => {
        setActivityAttributeRef.current?.(openedId)
    }, [openedId])
    const intro =
        introOpen && run.state.phase === "working"
            ? { kind: introKind, onDone: () => setIntroDoneFor(introActivityId) }
            : null
    const [ui, dispatchUi] = useStudentActivityState()

    useEffect(() => {
        dispatchUi({ type: "RESET" })
    }, [run.activityId, dispatchUi])

    /* ── l'appareil photo : une page par prise, comme aujourd'hui, vers l'activité figée au geste ── */
    const scanner = useCopyScanner(run)

    /* ── l'énoncé ── */
    // Le support relu à l'ouverture d'abord : l'activité reçue a pu partir d'une liste chargée avant
    // que son énoncé arrive, et l'exécution montrait alors « Ajouter un énoncé » quand le rendu, relu,
    // montrait l'énoncé.
    const material = (run.material as ApiRunActivity["material"] | null) ?? activity?.material ?? null
    const figure = useStatementFigure(material)
    const statementActivity = useMemo(
        () => (activity != null && figure.material != null ? { ...activity, material: figure.material } : activity),
        [activity, figure.material]
    )
    const headingData = useMemo(() => toStudentActivityHeading(activity, subjects), [activity, subjects])
    const statement = useMemo(() => toStudentActivityStatement(statementActivity), [statementActivity])

    /* ── la copie ── */
    const copyModel = studentActivityCopy(run)
    const pieces = run.pieces

    // L'oral déjà enregistré se réécoute avant d'être rendu, comme dans l'ancienne boîte « Mon
    // travail » ; ouvrir l'enregistreur (le réenregistrer) l'arrête.
    const recordedUri = run.medium === "audio" ? (pieces[pieces.length - 1]?.uri ?? null) : null
    const player = useConversationVoicePlayer(
        recordedUri != null && !ui.recorderOpen ? `copy-${run.activityId ?? "none"}` : null
    )
    const recordedVoice = useMemo<UIConversationMessage | null>(
        () =>
            recordedUri != null
                ? {
                      id: `copy-${recordedUri}`,
                      author: "self",
                      attachment: { kind: "voice", uri: recordedUri, durationLabel: "" },
                  }
                : null,
        [recordedUri]
    )

    const capture = useCallback(() => {
        trackCopyCapture(run.medium, pieces.length)
        if (run.medium === "audio") dispatchUi({ type: "OPEN_RECORDER" })
        else if (Platform.OS === "web") {
            if (scanner.openCamera()) dispatchUi({ type: "OPEN_CAMERA" })
        } else scanner.launchScanner()
    }, [run.medium, pieces.length, scanner, dispatchUi])

    const page: StudentActivityPageProps = {
        title: headingData.title,
        subject: headingData.subject,
        chapterName: headingData.chapterName,
        chrono: run.chrono,
        heading,
        onBack: canGoBack ? run.requestBack : undefined,
        // « Revoir le quiz » : la tentative faite, en live comme hors live (l'ancien en-tête l'offrait aux deux).
        onReviewQuiz: quiz.canReview ? quiz.review.open : undefined,
        statement,
        figurePending: figure.figurePending,
        onStatementPdfError: (cause) =>
            statementTelemetry.error("pdf load", cause, { material_id: activity?.material?.id ?? null }),
        instruction: run.stepDescriptions[0] ?? undefined,
        addMaterial:
            addMaterial != null && run.activityId != null
                ? {
                      label: addMaterialLabel(activity?.slug),
                      onPress: () => addMaterial(run.activityId as number),
                  }
                : undefined,
        copy: {
            ...copyModel,
            onOpenPhoto: (index) => dispatchUi({ type: "OPEN_PHOTO", index }),
            onRetryPhoto: (photo) => {
                const piece = pieces.find((candidate) => candidate.key === photo.id)
                if (piece?.outboxId != null) run.retryPiece(piece.outboxId)
            },
            onCapture: capture,
            onOpenRecording: () => dispatchUi({ type: "OPEN_RECORDER" }),
            playback:
                recordedVoice != null
                    ? {
                          playing: player.messageId === recordedVoice.id && player.playing,
                          onTogglePlay: () => player.toggle(recordedVoice),
                      }
                    : undefined,
            onRerecord:
                run.medium === "audio" && copyModel.recording != null && !run.copyInFlight
                    ? () => {
                          run.discardRecording()
                          dispatchUi({ type: "OPEN_RECORDER" })
                      }
                    : undefined,
            onFinish: () => {
                trackButton(ACTIVITY_BUTTONS.submit)
                run.finish()
            },
        },
        openPhoto: ui.openPhoto,
        onClosePhoto: () => dispatchUi({ type: "CLOSE_PHOTO" }),
        onDeletePhoto: (index) => {
            const piece = pieces[index]
            if (piece != null) dispatchUi({ type: "ASK_DELETE", key: piece.key })
        },
        confirmDelete: ui.confirmDelete != null,
        onConfirmDelete: () => {
            if (ui.confirmDelete != null) run.deletePiece(ui.confirmDelete)
            dispatchUi({ type: "DELETE_DONE" })
        },
        onCancelDelete: () => dispatchUi({ type: "CANCEL_DELETE" }),
    }

    /* ── les boîtes : l'échec de « J'ai fini », la sortie, l'activité retirée, la suppression ratée ── */
    const failure = finishFailureDialog(run.state.failure, {
        copyFailures: run.state.copyFailures,
        live: live != null,
    })
    const exit = exitDialog(run.state.exitQuestion)
    const dialog: StudentActivityViewDialog | null =
        failure != null
            ? trackedDialog(failure, run.finish, run.dismissFailure)
            : exit != null
              ? trackedDialog(exit, run.confirmExit, run.cancelBack)
              : run.notice === "delete_failed"
                ? trackedDialog(DELETE_FAILED_DIALOG, run.clearNotice, run.clearNotice)
                : null

    /* ── la suite : la correction, les félicitations, l'activité suivante ── */
    const nextActivity = useMemo(() => {
        if (options.nextActivityId != null) return { id: options.nextActivityId } as ApiRunActivity
        const remaining = remainingTodo(user?.todo_activities, run.activityId)
        return (getMostRecentActivity(remaining, null) as ApiRunActivity | null) ?? null
    }, [options.nextActivityId, user?.todo_activities, run.activityId])

    // Le mot du champion se tire une fois par activité finie, comme l'ancien écran de fin (gardé au
    // montage) : en live, la salle et le chat font rendre l'écran sans cesse, et un tirage par rendu
    // changerait le mot sous les yeux de l'élève.
    const inCongrats = run.state.phase === "congrats"
    const celebration = useMemo(
        () =>
            inCongrats
                ? studentActivityCelebration({
                      user,
                      finishedToday: getTodaysFinishedActivities(user) as { finished_at?: string | null }[],
                      withSam,
                  })
                : null,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [inCongrats, run.activityId]
    )
    const congrats: StudentActivityCongratsModel | null =
        celebration != null
            ? studentActivityCongratsModel({
                  result: run.state.result,
                  celebration,
                  withSam,
                  hasNext: nextActivity != null,
                  stays: run.state.stays,
              })
            : null

    // Soumise en Dialogue, une copie que Sam corrige : sa correction ne s'attend pas, le live la surveille
    // et l'annonce à son arrivée (« Ta correction de … est prête », sans son), et l'activité rendue la
    // montre dès qu'elle est là (Wajih, 26/10).
    const watchesCorrection = run.state.stays && run.state.result?.correction_wait != null
    const watchCorrection = live?.correctionLeft
    useEffect(() => {
        if (watchesCorrection && run.activityId != null) {
            watchCorrection?.({ id: run.activityId, title: headingData.title })
        }
        // Une fois par copie soumise : la surveillance ne repart pas à chaque rendu du live.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchesCorrection, run.activityId])

    /*
     * L'activité ouverte est déjà rendue : elle se relit (`StudentFinishedActivity`), elle ne s'exécute
     * pas. Aussi celle que le tuteur vient de rendre à la place de l'élève (« Soumettre ») : l'ancien
     * écran passait à sa relecture, `finished_at` posé ; et celle que l'élève vient de soumettre en
     * Dialogue, où il revient après ses félicitations. Gardée d'un rendu à l'autre : le live se redessine
     * sans cesse, et la relecture se recomposerait à chaque fois.
     */
    const rereadPhase = run.state.phase === "validated" || run.state.phase === "submitted"
    const finished = useMemo<ApiFinishedActivity | null>(() => {
        if (opened == null) return null
        if (finishedAtOpen) return opened as ApiFinishedActivity
        if (!rereadPhase) return null
        return { ...opened, finished_at: opened.finished_at ?? new Date().toISOString() } as ApiFinishedActivity
    }, [opened, finishedAtOpen, rereadPhase])

    return {
        run,
        page,
        /** Quel énoncé la page montre (le support, à défaut l'activité) : la lecture de l'élève s'y règle. */
        statementKey: material?.id ?? activity?.id ?? null,
        dialog,
        deletedDialog: run.state.phase === "deleted" ? DELETED_DIALOG : null,
        recorder: ui.recorderOpen
            ? {
                  onClose: () => dispatchUi({ type: "CLOSE_RECORDER" }),
                  // Une activité orale rend UN enregistrement : le nouveau remplace celui d'avant.
                  onSend: (file: CopyFile) => {
                      run.discardRecording()
                      run.capture(file)
                  },
                  onOpened: run.recorderOpened,
                  onClosed: run.recorderClosed,
                  paused: !!live?.paused,
                  liveCallActive: live?.liveSessionId != null,
              }
            : null,
        /** La caméra du navigateur ouverte : une page de la copie à prendre. */
        camera: ui.cameraOpen
            ? {
                  inLive: live != null,
                  onPhoto: scanner.cameraPhoto,
                  onClose: () => {
                      scanner.cameraClosed()
                      dispatchUi({ type: "CLOSE_CAMERA" })
                  },
              }
            : null,
        correction:
            run.state.phase === "correction" && run.state.result?.activity != null
                ? {
                      activity: run.state.result.activity,
                      questions: run.state.result.correction_wait?.questions ?? [],
                      /** L'attente quittée avant la correction : le live l'annoncera à son arrivée. */
                      onLeftUnsettled:
                          live?.correctionLeft != null && run.activityId != null
                              ? () => live.correctionLeft?.({ id: run.activityId as number, title: page.title })
                              : undefined,
                  }
                : null,
        congrats,
        nextActivity,
        /** La porte du quiz de prérequis : `blocking`, la feuille à poser. */
        quiz,
        /** L'entrée de l'activité à jouer (l'annonce, le décompte) ; `null` : l'énoncé. */
        intro,
        finished,
        /** « Refaire l'activité » sur l'activité relue dans le live : le live ouvre la nouvelle. */
        onReplayed: live?.openReplayed,
        heading,
        /** L'activité s'exécute dans le live : ses sons prennent la branche du live. */
        inLive: live != null,
    }
}

export type StudentActivityView = ReturnType<typeof useStudentActivityView>
